import { useCallback } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { SlideOut, SlideOutHeader } from '@aimsos/ui';

import { useLogInteraction } from '../../hooks/useLogInteraction.js';
import InteractionForm from './InteractionForm.jsx';

import { Phone, Users } from 'lucide-react';

/**
 * Log a call / Log a meeting (CT-5724): a `SlideOut` like the note form, mounted
 * only while open so each opening starts clean. As in the prototype, the header
 * says which record it joins ("On <name>. It joins this record's history as a
 * call, and counts as one.") and the subject starts as "Call with <name>".
 *
 * @param {object} props
 * @param {'call'|'meeting'} props.kind
 * @param {string} props.entityType
 * @param {string} props.entityId
 * @param {string} props.entityName — the record as the page names it.
 * @param {() => void} props.onClose
 * @param {() => void} props.onLogged — the Timeline refreshes.
 */
function LogInteractionSlideOut({ kind, entityType, entityId, entityName, onClose, onLogged }) {
  const { t } = useTranslation('unified-profiles');
  const title = t(`history.log.${kind}.title`);
  const subtitle = t(`history.log.${kind}.subtitle`, { name: entityName });
  const defaultSubject = t(`history.log.${kind}.subjectDefault`, { name: entityName });

  const handleLogged = useCallback(() => {
    onLogged?.();
    onClose();
  }, [onLogged, onClose]);
  const { log, saving } = useLogInteraction({ entityType, entityId, onLogged: handleLogged });
  const handleSubmit = useCallback((values) => log(kind, values), [log, kind]);

  const renderHeader = useCallback(
    (titleId) => (
      <SlideOutHeader
        titleId={titleId}
        title={title}
        subtitle={subtitle}
        icon={kind === 'meeting' ? Users : Phone}
        size="M"
        showClose
        onClose={onClose}
      />
    ),
    [title, subtitle, kind, onClose],
  );

  return (
    <SlideOut isOpen onClose={onClose} renderHeader={renderHeader} size="M" ariaLabel={title}>
      <div className="scrollbar-aimsos-auto h-full min-h-0 overflow-y-auto">
        <InteractionForm
          kind={kind}
          defaultSubject={defaultSubject}
          saving={saving}
          onSubmit={handleSubmit}
          onCancel={onClose}
        />
      </div>
    </SlideOut>
  );
}

export default LogInteractionSlideOut;
